import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useActiveCustomerId } from '../contexts/AccountContext';
import { useViewAs } from '../contexts/ViewAsContext';
import type { CalendarTrip } from '../lib/tripCalendar';

export interface VisitPlan {
  id: string;
  customer_id: string;
  location_id: string | null;
  title: string;
  notes: string | null;
  start_date: string;
  end_date: string;
  created_at: string;
  persisted: boolean;
}

export interface CreateVisitPlanInput {
  title: string;
  notes?: string;
  start_date: string;
  end_date: string;
  location_id?: string | null;
}

function isMissingTable(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return error.code === 'PGRST205' || error.code === '42P01' || /could not find the table/i.test(error.message ?? '');
}

let remoteUnavailable = false;

function storageKey(customerId: string): string {
  return `pazportal:visit-plans:${customerId}`;
}

function loadLocal(customerId: string): VisitPlan[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey(customerId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as VisitPlan[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocal(customerId: string, plans: VisitPlan[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(storageKey(customerId), JSON.stringify(plans));
}

export function loadVisitPlansForCustomers(customerIds: string[]): VisitPlan[] {
  const seen = new Set<string>();
  const plans: VisitPlan[] = [];
  for (const customerId of customerIds) {
    for (const plan of loadLocal(customerId)) {
      if (seen.has(plan.id)) continue;
      seen.add(plan.id);
      plans.push(plan);
    }
  }
  return plans;
}

export function usePortfolioVisitPlans(customerIds: string[]) {
  const ids = [...new Set(customerIds.filter(Boolean))].sort();

  return useQuery({
    queryKey: ['visit-plans-portfolio', ids],
    enabled: ids.length > 0,
    queryFn: async (): Promise<VisitPlan[]> => {
      const local = loadVisitPlansForCustomers(ids);
      if (remoteUnavailable) return local;

      const { data, error } = await supabase
        .from('visit_plans')
        .select('id, customer_id, location_id, title, notes, start_date, end_date, created_at')
        .in('customer_id', ids)
        .is('deleted_at', null)
        .order('start_date', { ascending: true });

      if (error) {
        if (isMissingTable(error)) remoteUnavailable = true;
        return local;
      }

      const remote: VisitPlan[] = (data ?? []).map((row) => ({
        ...row,
        persisted: true,
      }));
      const remoteIds = new Set(remote.map((row) => row.id));
      const unsynced = local.filter((row) => !row.persisted && !remoteIds.has(row.id));
      return [...remote, ...unsynced];
    },
    staleTime: 30 * 1000,
  });
}

export function visitPlansToCalendarTrips(plans: VisitPlan[]): CalendarTrip[] {
  return plans.map((plan) => ({
    id: `planned:${plan.id}`,
    title: plan.title,
    start: plan.start_date,
    end: plan.end_date,
    source: 'planned',
    status: 'scheduled',
    notes: plan.notes,
  }));
}

/**
 * Visit plans for one customer. Defaults to the active account; pass
 * `customerIdOverride` on pages that already know the customer (a site
 * detail page in portfolio mode has no single active account).
 */
export function useVisitPlans(customerIdOverride?: string | null) {
  const activeCustomerId = useActiveCustomerId();
  const customerId = customerIdOverride ?? activeCustomerId;
  const { isStaff, viewAsUserId } = useViewAs();
  // Staff in view-as mode are read-only: refuse before touching localStorage
  // so nothing "saved" ever exists only in the staff member's browser.
  const readOnly = isStaff && !!viewAsUserId;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['visit-plans', customerId],
    enabled: Boolean(customerId),
    queryFn: async (): Promise<VisitPlan[]> => {
      const local = loadLocal(customerId!);
      if (remoteUnavailable) return local;

      const { data, error } = await supabase
        .from('visit_plans')
        .select('id, customer_id, location_id, title, notes, start_date, end_date, created_at')
        .eq('customer_id', customerId!)
        .is('deleted_at', null)
        .order('start_date', { ascending: true });

      if (error) {
        if (isMissingTable(error)) remoteUnavailable = true;
        return local;
      }

      const remote: VisitPlan[] = (data ?? []).map((row) => ({
        ...row,
        persisted: true,
      }));
      const remoteIds = new Set(remote.map((row) => row.id));
      const unsynced = local.filter((row) => !row.persisted && !remoteIds.has(row.id));
      return [...remote, ...unsynced];
    },
    staleTime: 30 * 1000,
  });

  const create = useMutation({
    mutationFn: async (input: CreateVisitPlanInput): Promise<VisitPlan> => {
      if (readOnly) throw new Error('Read-only while viewing as a customer');
      if (!customerId) throw new Error('No active account');
      const local: VisitPlan = {
        id: crypto.randomUUID(),
        customer_id: customerId,
        location_id: input.location_id ?? null,
        title: input.title.trim(),
        notes: input.notes?.trim() || null,
        start_date: input.start_date,
        end_date: input.end_date,
        created_at: new Date().toISOString(),
        persisted: false,
      };

      const pending = [...loadLocal(customerId), local];
      saveLocal(customerId, pending);

      if (remoteUnavailable) return local;

      const { data, error } = await supabase
        .from('visit_plans')
        .insert({
          customer_id: customerId,
          location_id: input.location_id || null,
          title: local.title,
          notes: local.notes,
          start_date: local.start_date,
          end_date: local.end_date,
        })
        .select('id, customer_id, location_id, title, notes, start_date, end_date, created_at')
        .single();

      if (error || !data) {
        // The local copy exists for ONE situation: the visit_plans table is not
        // deployed yet, so the planner keeps working on this device until it is.
        // It used to be returned for every error — an RLS rejection, a check
        // constraint, a dropped connection — and nothing in the UI reads
        // `persisted`, so a plan the database refused rendered exactly like a
        // saved one. The customer believed PazNetworks could see a visit that
        // lived only in their browser. Any other failure now undoes the pending
        // row and fails the mutation, and the calendar keeps the form open with
        // the error, so nothing the customer typed is lost.
        if (isMissingTable(error)) {
          remoteUnavailable = true;
          return local;
        }
        saveLocal(
          customerId,
          loadLocal(customerId).filter((row) => row.id !== local.id),
        );
        // The raw Postgres text names constraints and relations; it is for the
        // console, not the customer.
        console.error('visit plan insert failed', error);
        throw new Error('Could not save this visit. Please try again.');
      }

      const persisted: VisitPlan = { ...data, persisted: true };
      saveLocal(
        customerId,
        pending.filter((row) => row.id !== local.id),
      );
      return persisted;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visit-plans', customerId] });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (readOnly || !customerId) return;
      saveLocal(
        customerId,
        loadLocal(customerId).filter((row) => row.id !== id),
      );
      if (remoteUnavailable) return;
      await supabase
        .from('visit_plans')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .eq('customer_id', customerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visit-plans', customerId] });
    },
  });

  return {
    plans: query.data ?? [],
    isLoading: query.isLoading,
    create,
    remove,
  };
}
